import { requireClient } from '../matrix/client'

interface EncryptedFileInfo {
  url: string
  key: {
    alg: string
    k: string
    key_ops: string[]
    kty: string
    ext: boolean
  }
  iv: string
  hashes: Record<string, string>
  v: string
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

const blobUrlCache = new Map<string, string>()

async function fetchMedia(mxcUrl: string): Promise<ArrayBuffer> {
  const client = requireClient()
  const accessToken = client.getAccessToken()
  const headers: Record<string, string> = accessToken ? { Authorization: `Bearer ${accessToken}` } : {}

  const match = mxcUrl.match(/^mxc:\/\/([^/]+)\/(.+)$/)
  if (!match) throw new Error('Invalid mxc URL')
  const serverName = match[1]!
  const mediaId = match[2]!

  const paths = [
    `/_matrix/client/v1/media/download/${serverName}/${mediaId}`,
    `/_matrix/media/v3/download/${serverName}/${mediaId}`,
  ]

  for (const path of paths) {
    try {
      const response = await fetch(path, { headers })
      if (response.ok) return response.arrayBuffer()
      if (response.status !== 404) throw new Error(`Download failed: ${response.status}`)
    } catch (err) {
      if (err instanceof TypeError) continue
      throw err
    }
  }

  throw new Error('Media not available from any endpoint')
}

export async function decryptAttachment(fileInfo: EncryptedFileInfo): Promise<string> {
  const cacheKey = fileInfo.url
  const cached = blobUrlCache.get(cacheKey)
  if (cached) return cached

  const encryptedData = await fetchMedia(fileInfo.url)

  const cryptoKey = await crypto.subtle.importKey(
    'jwk',
    {
      alg: fileInfo.key.alg,
      ext: fileInfo.key.ext,
      k: fileInfo.key.k,
      key_ops: ['decrypt'],
      kty: fileInfo.key.kty,
    },
    { name: 'AES-CTR' },
    false,
    ['decrypt']
  )

  const iv = base64ToArrayBuffer(fileInfo.iv)
  const ivArray = new Uint8Array(iv)
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CTR', counter: ivArray, length: 64 },
    cryptoKey,
    encryptedData
  )

  const blob = new Blob([decrypted])
  const blobUrl = URL.createObjectURL(blob)
  blobUrlCache.set(cacheKey, blobUrl)
  return blobUrl
}

export async function fetchMediaUrl(mxcUrl: string): Promise<string> {
  const cached = blobUrlCache.get(mxcUrl)
  if (cached) return cached

  const data = await fetchMedia(mxcUrl)
  const blob = new Blob([data])
  const blobUrl = URL.createObjectURL(blob)
  blobUrlCache.set(mxcUrl, blobUrl)
  return blobUrl
}

export function getMediaUrl(content: Record<string, unknown>): { url: string | null; encrypted: boolean; fileInfo?: EncryptedFileInfo } {
  const file = content.file as EncryptedFileInfo | undefined
  if (file?.url) {
    return { url: file.url, encrypted: true, fileInfo: file }
  }
  const url = content.url as string | undefined
  return { url: url ?? null, encrypted: false }
}
